import { warehouseLabel } from '@/lib/warehouseLabel';
import { format, max, parseISO, subYears } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { z } from 'zod';
import {
  PURCHASE_ORDER_LINE_SORT_KEYS,
  PURCHASE_ORDER_LINE_STATES,
  type ListPurchaseOrderLinesInput,
  type ListPurchaseOrderLinesResponse,
  type PurchaseOrderLineRow,
  type PurchaseOrderLineSortKey,
  type PurchaseOrderLineState,
} from '@erp/rpc';
import type { MultiSelectOption } from '@/components/common/MultiSelect';

/**
 * Purchase Order Status's own search params (one row per PO line). The shared
 * procurement filters it also applies (`divisionIds`, `warehouseIds`,
 * `stockStatuses`, `skuSearch`) come from the procurement filter route above
 * it, so they carry to and from the forecast screens and the buyer board.
 *
 * A malformed value is dropped rather than failing the route, so an old
 * bookmark still opens the page.
 */
export const purchaseOrderLinesSearchSchema = z.object({
  /**
   * AS400 PO numbers as typed or pasted: comma or space separated. The router
   * reads a bare `?poNumbers=3202` as the number 3202, so numbers are taken too.
   */
  poNumbers: z
    .union([z.string(), z.number()])
    .transform(String)
    .optional()
    .catch(undefined),
  supplierId: z.uuid().optional().catch(undefined),
  buyerCodes: z.array(z.string()).optional().catch(undefined),
  /** Absent = open lines only (see `lineStatesOf`). */
  states: z
    .array(z.enum(PURCHASE_ORDER_LINE_STATES))
    .optional()
    .catch(undefined),
  /** Absent = lines created over `HIDE_OLD_LINES_YEARS` ago are hidden. */
  showOldLines: z.boolean().optional().catch(undefined),
  orderedDateFrom: z.iso.date().optional().catch(undefined),
  orderedDateTo: z.iso.date().optional().catch(undefined),
  expectedDateFrom: z.iso.date().optional().catch(undefined),
  expectedDateTo: z.iso.date().optional().catch(undefined),
  sortBy: z.enum(PURCHASE_ORDER_LINE_SORT_KEYS).optional().catch(undefined),
  sortDirection: z.enum(['asc', 'desc']).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(1).max(200).optional().catch(undefined),
});

export type PurchaseOrderLinesSearch = z.infer<
  typeof purchaseOrderLinesSearchSchema
>;

/** Buyers open this page to chase what is still due, so that is the default. */
export const DEFAULT_LINE_STATES: readonly PurchaseOrderLineState[] = ['open'];

export function lineStatesOf(
  search: Pick<PurchaseOrderLinesSearch, 'states'>,
): readonly PurchaseOrderLineState[] {
  return search.states ?? DEFAULT_LINE_STATES;
}

/**
 * AS400 keeps lines open for decades (some date from 1998), and they would top
 * the oldest-first default. Lines created more than this long ago are hidden
 * unless the buyer asks for them. Keyed on the create date, not the expected
 * date: AS400 writes an empty expected date as 1970-01-01, which would hide
 * orders placed this year.
 */
export const HIDE_OLD_LINES_YEARS = 2;

/**
 * The create-date lower bound to send: the buyer's own "from", raised to the
 * old-lines cutoff while those lines are hidden.
 */
export function effectiveOrderedDateFrom(
  search: Pick<PurchaseOrderLinesSearch, 'orderedDateFrom' | 'showOldLines'>,
  today: Date,
): string | undefined {
  if (search.showOldLines) return search.orderedDateFrom;
  const cutoff = subYears(today, HIDE_OLD_LINES_YEARS);
  const from = search.orderedDateFrom
    ? max([parseISO(search.orderedDateFrom), cutoff])
    : cutoff;
  return format(from, 'yyyy-MM-dd');
}

/** The backend matches at most this many PO numbers at once. */
export const MAX_PO_NUMBERS = 50;

/** Distinct, non-empty PO numbers from the search box. */
export function parsePoNumbers(value: string | undefined): string[] {
  if (!value) return [];
  const tokens = value.split(/[\s,]+/).filter((t) => t.length > 0);
  return [...new Set(tokens)].slice(0, MAX_PO_NUMBERS);
}

/**
 * Purchase Order Status's "Clear filters" target. The page sits under the
 * procurement filter route, which gives a navigation back any shared filter it
 * leaves out, so the shared filters this page shows are cleared by name. The
 * shared filters it does not show carry on untouched; its own params are
 * dropped by leaving them out.
 */
export function clearedPurchaseOrderSearch(): {
  divisionIds: undefined;
  warehouseIds: undefined;
  stockStatuses: undefined;
  skuSearch: undefined;
} {
  return {
    divisionIds: undefined,
    warehouseIds: undefined,
    stockStatuses: undefined,
    skuSearch: undefined,
  };
}

// ─── Page state ─────────────────────────────────────────────────────────────

/** This page's own params plus the shared procurement filters it applies. */
export type PurchaseOrderLinesPageSearch = PurchaseOrderLinesSearch & {
  divisionIds?: string[];
  warehouseIds?: string[];
  stockStatuses?: string[];
  skuSearch?: string;
};

export const DEFAULT_PAGE_SIZE = 50;

export function nonEmpty<T>(values: readonly T[] | undefined): T[] | undefined {
  return values && values.length > 0 ? [...values] : undefined;
}

const SORT_KEYS: ReadonlySet<string> = new Set(PURCHASE_ORDER_LINE_SORT_KEYS);

export function isSortKey(id: string): id is PurchaseOrderLineSortKey {
  return SORT_KEYS.has(id);
}

export function sameStates(
  a: readonly PurchaseOrderLineState[],
  b: readonly PurchaseOrderLineState[],
): boolean {
  const inB = new Set(b);
  return new Set(a).size === inB.size && a.every((s) => inB.has(s));
}

/** Paging and sort, defaulting to 50 rows sorted oldest created first. */
export function pageStateOf(search: PurchaseOrderLinesPageSearch): {
  pageIndex: number;
  pageSize: number;
  sortBy: PurchaseOrderLineSortKey;
  sortDirection: 'asc' | 'desc';
} {
  return {
    pageIndex: (search.page ?? 1) - 1,
    pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE,
    sortBy: search.sortBy ?? 'orderedDate',
    sortDirection: search.sortDirection ?? 'asc',
  };
}

/** The list request for what the URL says, as of `today`. */
export function lineQueryInput(
  search: PurchaseOrderLinesPageSearch,
  today: Date,
): ListPurchaseOrderLinesInput {
  const { pageIndex, pageSize, sortBy, sortDirection } = pageStateOf(search);
  return {
    poNumbers: nonEmpty(parsePoNumbers(search.poNumbers)),
    supplierId: search.supplierId,
    buyerCodes: nonEmpty(search.buyerCodes),
    stockStatuses: nonEmpty(search.stockStatuses),
    divisionIds: nonEmpty(search.divisionIds),
    warehouseIds: nonEmpty(search.warehouseIds),
    skuSearch: search.skuSearch?.trim() || undefined,
    states: nonEmpty(lineStatesOf(search)),
    orderedDateFrom: effectiveOrderedDateFrom(search, today),
    orderedDateTo: search.orderedDateTo,
    expectedDateFrom: search.expectedDateFrom,
    expectedDateTo: search.expectedDateTo,
    sortBy,
    sortDirection,
    limit: pageSize,
    offset: pageIndex * pageSize,
  };
}

const VALUE_FILTERS = [
  'poNumbers',
  'skuSearch',
  'supplierId',
  'orderedDateFrom',
  'orderedDateTo',
  'expectedDateFrom',
  'expectedDateTo',
  'sortBy',
  'showOldLines',
] as const satisfies readonly (keyof PurchaseOrderLinesPageSearch)[];

const LIST_FILTERS = [
  'buyerCodes',
  'stockStatuses',
  'divisionIds',
  'warehouseIds',
] as const satisfies readonly (keyof PurchaseOrderLinesPageSearch)[];

/** Anything set away from the default view, which shows "Clear filters". */
export function hasActiveFilters(
  search: PurchaseOrderLinesPageSearch,
): boolean {
  return (
    VALUE_FILTERS.some((key) => Boolean(search[key])) ||
    LIST_FILTERS.some((key) => (search[key]?.length ?? 0) > 0) ||
    !sameStates(lineStatesOf(search), DEFAULT_LINE_STATES)
  );
}

// ─── Filter controls ────────────────────────────────────────────────────────

/**
 * The `states` param for the statuses picked in the filter: absent for the
 * default (open), an empty list for none picked (every state).
 */
export function statesParamFor(
  pickedIds: readonly string[],
): PurchaseOrderLineState[] | undefined {
  const picked = new Set(pickedIds);
  const states = PURCHASE_ORDER_LINE_STATES.filter((s) => picked.has(s));
  return sameStates(states, DEFAULT_LINE_STATES) ? undefined : states;
}

/** The options whose id is picked, in the options' order. */
export function selectedOptions<T extends { id: string }>(
  options: readonly T[],
  ids: readonly string[] | undefined,
): T[] {
  const picked = new Set(ids);
  return options.filter((o) => picked.has(o.id));
}

/** Options for a code filter, keeping any picked value the list lacks. */
export function codeOptions(
  codes: readonly string[] = [],
  selected: readonly string[] = [],
): MultiSelectOption[] {
  return [...new Set([...codes, ...selected])].map((code) => ({
    id: code,
    label: code,
  }));
}

/**
 * The location filters' options from what the lookups returned so far (either
 * may still be loading), plus the divisions whose buyer codes to offer: the
 * picked ones, or every division when none is picked.
 */
export function locationOptionsOf(
  divisions: { divisions: readonly { id: string; name: string }[] } | undefined,
  warehouses: { warehouses: readonly WarehouseChoice[] } | undefined,
  pickedDivisionIds: readonly string[] = [],
): {
  divisionOptions: MultiSelectOption[];
  warehouseOptions: MultiSelectOption[];
  buyerCodeDivisionIds: string[];
} {
  const divisionOptions = (divisions?.divisions ?? []).map((d) => ({
    id: d.id,
    label: d.name,
  }));
  return {
    divisionOptions,
    warehouseOptions: warehouseOptionsFor(
      warehouses?.warehouses ?? [],
      pickedDivisionIds,
    ),
    buyerCodeDivisionIds:
      pickedDivisionIds.length > 0
        ? [...pickedDivisionIds]
        : divisionOptions.map((o) => o.id),
  };
}

/**
 * The picked locations that stay valid once the division filter changes. A
 * location outside the new divisions would drop out of the picker yet keep
 * filtering every line away, so it goes. Kept as-is while the locations load.
 */
export function warehouseIdsWithin(
  warehouses: { warehouses: readonly WarehouseChoice[] } | undefined,
  divisionIds: readonly string[] | undefined,
  pickedWarehouseIds: readonly string[] | undefined,
): string[] | undefined {
  if (!warehouses) return nonEmpty(pickedWarehouseIds);
  const options = warehouseOptionsFor(warehouses.warehouses, divisionIds ?? []);
  return nonEmpty(
    selectedOptions(options, pickedWarehouseIds).map((o) => o.id),
  );
}

/** The table's rows and page count, empty while the first page loads. */
export function tableStateOf(
  data: ListPurchaseOrderLinesResponse | undefined,
  pageSize: number,
): { rows: PurchaseOrderLineRow[]; rowCount: number; pageCount: number } {
  const rowCount = data?.total ?? 0;
  return {
    rows: data?.rows ?? [],
    rowCount,
    pageCount: Math.ceil(rowCount / pageSize),
  };
}

interface WarehouseChoice {
  id: string;
  name: string;
  displayCode?: string | null;
  division: { id: string; name: string } | null;
}

/** Locations inside the picked divisions (every location when none is picked). */
export function warehouseOptionsFor(
  warehouses: readonly WarehouseChoice[],
  divisionIds: readonly string[],
): MultiSelectOption[] {
  const scope = new Set(divisionIds);
  const options: MultiSelectOption[] = [];
  for (const w of warehouses) {
    if (scope.size > 0 && !(w.division && scope.has(w.division.id))) continue;
    options.push({
      id: w.id,
      label: warehouseLabel(w),
      group: w.division?.name,
    });
  }
  return options;
}

export function toDateRange(from?: string, to?: string): DateRange | undefined {
  if (!from && !to) return undefined;
  return {
    from: from ? parseISO(from) : undefined,
    to: to ? parseISO(to) : undefined,
  };
}

export function fromDateRange(range: DateRange | undefined): {
  from?: string;
  to?: string;
} {
  return {
    from: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
    to: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
  };
}
